import { useEffect, useState } from 'react';

interface PasswordStrengthMeterProps {
  password: string;
  className?: string;
}

export function PasswordStrengthMeter({ password, className }: PasswordStrengthMeterProps) {
  const [strength, setStrength] = useState(0);

  useEffect(() => {
    // Calculate strength (0-4 scale)
    let score = 0;
    
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;
    
    setStrength(Math.min(score, 4));
  }, [password]);

  const getStrengthLabel = () => {
    if (!password) return '';
    if (strength === 0) return 'Very Weak';
    if (strength === 1) return 'Weak';
    if (strength === 2) return 'Fair';
    if (strength === 3) return 'Good';
    return 'Strong';
  };

  const getStrengthColor = () => {
    if (strength === 0) return 'bg-red-500';
    if (strength === 1) return 'bg-orange-500';
    if (strength === 2) return 'bg-yellow-500';
    if (strength === 3) return 'bg-blue-500';
    return 'bg-green-500';
  };

  if (!password) return null;

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-1">
        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getStrengthColor()}`}
            style={{ width: `${(strength / 4) * 100}%` }}
          />
        </div>
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 min-w-[70px]">
          {getStrengthLabel()}
        </span>
      </div>
      
      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
        {password.length < 12 && <div>• At least 12 characters</div>}
        {!/[a-z]/.test(password) && <div>• Include lower case letter</div>}
        {!/[A-Z]/.test(password) && <div>• Include upper case letter</div>}
        {!/\d/.test(password) && <div>• Include number</div>}
        {!/[^a-zA-Z\d]/.test(password) && <div>• Include special character</div>}
      </div>
    </div>
  );
}
